import { useCallback } from "react";
import { Smartphone, AlertCircle } from "lucide-react";
import { EmptyState } from "@/components/admin/shared/EmptyState";
import {
  usePaymentKPIs,
  usePaymentTransactions,
} from "@/hooks/admin/usePayments";
import {
  getMethodLabel,
  getMethodClass,
  getStatusLabel,
  getStatusClass,
} from "./constants";
import { formatFcfa, formatDate } from "./formatters";

export function MobileMoneyTab() {
  const {
    data: kpis,
    isLoading,
    isError: isKpiError,
    refetch: refetchKpis,
  } = usePaymentKPIs();
  const {
    data: txData,
    isError: isTxError,
    refetch: refetchTx,
  } = usePaymentTransactions({ limit: 10 });
  const isError = isKpiError || isTxError;
  const retry = useCallback(() => {
    if (isKpiError) refetchKpis();
    if (isTxError) refetchTx();
  }, [isKpiError, isTxError, refetchKpis, refetchTx]);

  const providers = [
    {
      name: "Wave",
      volume: kpis?.wave_volume_fcfa ?? 0,
      color: "bg-blue-50 border-blue-200",
      textColor: "text-blue-700",
    },
    {
      name: "Orange Money",
      volume: kpis?.orange_money_volume_fcfa ?? 0,
      color: "bg-orange-50 border-orange-200",
      textColor: "text-orange-700",
    },
    {
      name: "Free Money",
      volume: 0,
      color: "bg-green-50 border-green-200",
      textColor: "text-green-700",
    },
  ];

  // Mobile Money tab excludes cash-on-delivery — match the DB-truth value.
  const recentMobileMoney = (txData?.payments ?? []).filter(
    (t) => t.method !== "cash_on_delivery",
  );

  return (
    <div className="space-y-6">
      {/* Error state */}
      {isError && (
        <div className="flex items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <AlertCircle className="h-4 w-4 shrink-0" />
          <span>Impossible de charger les données Mobile Money.</span>
          <button
            type="button"
            onClick={retry}
            className="ml-auto rounded border border-red-200 bg-white px-3 py-1 text-xs text-red-700 hover:bg-red-50"
          >
            Réessayer
          </button>
        </div>
      )}

      {/* Provider cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        {providers.map((p) => (
          <div
            key={p.name}
            className={`rounded-lg border p-5 ${p.color}`}
          >
            <p className={`text-sm font-semibold ${p.textColor}`}>{p.name}</p>
            {isLoading ? (
              <div className="mt-2 h-7 w-32 animate-pulse rounded bg-stone-200" />
            ) : (
              <p className="mt-2 font-display text-xl font-semibold text-stone-900">
                {formatFcfa(p.volume)}
              </p>
            )}
            <p className="mt-1 text-xs text-stone-500">Volume total</p>
          </div>
        ))}
      </div>

      {/* Recent transactions */}
      <div>
        <h3 className="mb-3 text-sm font-semibold text-stone-700">
          Transactions récentes — Mobile Money
        </h3>
        {recentMobileMoney.length === 0 ? (
          <EmptyState
            icon={<Smartphone className="h-5 w-5" />}
            title="Aucune transaction Mobile Money"
            description="Les transactions Wave, Orange Money et Free Money apparaîtront ici."
          />
        ) : (
          <div className="overflow-hidden rounded-lg border border-stone-200">
            <table className="w-full text-sm">
              <thead className="border-b border-stone-200 bg-stone-50">
                <tr>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-stone-500">
                    Référence
                  </th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-stone-500">
                    Méthode
                  </th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-stone-500">
                    Montant
                  </th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-stone-500">
                    Statut
                  </th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-stone-500">
                    Date
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100 bg-white">
                {recentMobileMoney.map((t) => (
                  <tr key={t.id} className="hover:bg-stone-50">
                    <td className="px-4 py-3 font-mono text-xs text-stone-700">
                      {t.ref || "—"}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`rounded-full px-2 py-0.5 text-xs font-medium ${getMethodClass(t.method)}`}
                      >
                        {getMethodLabel(t.method)}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-medium text-stone-900">
                      {formatFcfa(t.amount_fcfa)}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`rounded-full px-2 py-0.5 text-xs font-medium ${getStatusClass(t.status)}`}
                      >
                        {getStatusLabel(t.status)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-stone-500">
                      {formatDate(t.created_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
